import { useLocation } from '@reach/router';
import { SubscribeApi } from 'api/subscribeApi';
import { AppPath } from 'appConstants';
import { Dropdown, SelectOption } from 'components/dropdown/Dropdown';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import FormSchoolURNTextField from 'components/formComponents/FormSchoolURNTextField';
import FormTextField from 'components/formComponents/FormTextField';
import { PricingSummaryDetails } from 'components/pricingSummaryDetails/PricingSummaryDetails';
import { Spinner } from 'components/spinner/Spinner';
import { endpoints } from 'endpoints.config';
import { Form, Formik, FormikHelpers } from 'formik';
import { getWithParams } from 'helpers/endpointCalls';
import { useErrorToast } from 'hooks/useErrorToast';
import { useFetchSchoolInfo } from 'hooks/useFetchSchoolInfo';
import { ISchoolDetails } from 'pages/subscribe/models';
import { durationOptionsFromQuote, quotePackagesFromQuote } from 'pages/subscribe/quoteOptions';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectMembershipOptInfo,
  selectQuotePromoCodeSlots,
  selectSelectedPackages,
  setMembershipOptInfo,
  setQuotePromoCodeSlots,
  setSchoolDetails,
  updateSelectedPackages,
} from 'reducers/misc';
import { onlyDigitsBracketsSpacesDashesPluses } from 'utils/regex';
import * as Yup from 'yup';
import { PupilNumMismatchModal } from './PupilNumMismatchModal';
import { ErrorM } from 'components/formComponents/ErrorM';
import { ISchoolInfo } from 'pages/upgradeSubscription/models';
import { setQuotesId } from 'reducers/misc';
import { slotsFromQuotePackages } from 'subscriptionOptions/promoCodeSlots';

type SchoolDetailsFormProps = {
  onSubmit: () => void;
  hideSummaryPanel?: boolean;
};
export class SchoolDetailsParser {
  public static createEmptySchoolDetails = (
    schoolInfo: ISchoolInfo | null
  ): ISchoolDetails => { 
    return {
      schoolUrn: schoolInfo ? (schoolInfo.schoolUrn ?? "") : '',
      schoolname: schoolInfo ? schoolInfo.schoolName : '',
      phoneNumber: schoolInfo?.phoneNumber ?? '',
      addressLine1: schoolInfo?.addressLine1 ?? '',
      addressLine2: schoolInfo?.addressLine2 ?? '',
      addressLine3: schoolInfo?.addressLine3 ?? '',
      townCity: schoolInfo?.townCity ?? '',
      county: schoolInfo?.county ?? '',
      country: schoolInfo?.country ?? '',
      postcodeZipcode: schoolInfo?.postcodeZipcode ?? '',
      countryList: schoolInfo?.countryList ?? null,
    };
  };
};

const SchoolDetailsForm: React.FC<SchoolDetailsFormProps> = ({
  onSubmit,
  hideSummaryPanel,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const selectedPackages = useSelector(selectSelectedPackages);
  const membershipOptInfo: any = useSelector(selectMembershipOptInfo);
  /** The codes applied on the package step, kept per subject so this step prices what the user chose. */
  const promoCodeSlots = useSelector(selectQuotePromoCodeSlots);
  /** Named apart from the local `quotePackages` the re-save derives, which shadows it below. */
  const heldQuotePackages =
    membershipOptInfo && membershipOptInfo[0]
      ? membershipOptInfo[0].quotePackages
      : null;
  const validateSchoolURN =
    membershipOptInfo && membershipOptInfo[0].selectedLocationLabel
      ? membershipOptInfo[0].selectedLocationLabel.toLowerCase() !== 'other' &&
        membershipOptInfo[0].selectedLocationLabel.toLowerCase() !== 'non-uk eu'
      : true;
  const vatCountry = (membershipOptInfo && membershipOptInfo[0].vatCountry) || null;
  const selectedLocationLabel = membershipOptInfo && membershipOptInfo[0].selectedLocationLabel ? membershipOptInfo[0].selectedLocationLabel.toLowerCase() : '';
  const postcodeLabel =
    selectedLocationLabel === 'united kingdom' ? 'Postcode' : 'Postcode / postal code';

  const pathname = location.pathname;

  let { schoolInfo, error: schoolDetailsError } = useFetchSchoolInfo(
    pathname === AppPath.UPGRADE_SUBSCRIPTION
  );

  const [initialValues, setInitialValues] = useState(SchoolDetailsParser.createEmptySchoolDetails(schoolInfo));
  const [error, setError] = useState('');
  const [schoolUrnLoading, setSchoolUrnLoading] = useState<boolean>(false);
  const [schoolUrnList, setSchoolUrnList] = useState<any>([]);
  const [openPupilMismatchModal, setOpenPupilMismatchModal] =
    useState<boolean>(false);

  useEffect(() => {
    if(
      vatCountry &&
      initialValues.countryList &&
      !initialValues.country
    ){
      let initialValuesCopy = initialValues;
      let selectedVATCountry: any = initialValues.countryList.filter((country: any) => {
        return country.value === vatCountry;
      });

      initialValuesCopy.selectedCountry = selectedVATCountry[0];
      setInitialValues(initialValuesCopy);
    }
  }, [vatCountry, initialValues]);  

  useEffect(() => {
    setInitialValues(SchoolDetailsParser.createEmptySchoolDetails(schoolInfo));
  }, [schoolInfo]);    

  const schoolUrnFailedToast = useErrorToast(
    'Unable to find any schools with this URN.'
  );

  const validationSchema = Yup.object({
    schoolname: Yup.string().required('Please enter the school name'),
    phoneNumber: Yup.string()
      .required('Please enter the school phone number')
      .matches(
        onlyDigitsBracketsSpacesDashesPluses,
        'Please enter a valid phone number'
      ),
    addressLine1: Yup.string().required('Please enter the school address'),
    addressLine2: Yup.string(),
    addressLine3: Yup.string(),
    townCity: Yup.string().required('Please enter the school town/city'),
    county: Yup.string(),
    postcodeZipcode: Yup.string().required(
      'Please enter the school postcode/zipcode'
    ),
  }).test({
    name: 'school urn form validation',
    test: function (this: any, value: any) {
      const schoolUrn =
        value && value.hasOwnProperty('schoolUrn') ? value.schoolUrn : 0;

      if (validateSchoolURN && !schoolUrn) {
        return this.createError({
          path: 'schoolUrn',
          message: 'Please enter a school URN',
        });
      } else {
        return true;
      }
    },
  }).test({
    name: 'country list form validation',
    test: function (this: any, value: any) {
      const countrySelected =
        value && value.hasOwnProperty('selectedCountry')
          ? value.selectedCountry
          : null;

      if (
        pathname !== AppPath.UPGRADE_SUBSCRIPTION &&
        !countrySelected
      ) {
        return this.createError({
          path: 'country',
          message: 'Please select a country',
        });
      } else {
        return true;
      }
    },
  });

  const searchSchoolURN = async (search_urn_string: string) => {
    setSchoolUrnLoading(true);

    const result = await SubscribeApi.searchSchoolURN(search_urn_string);

    if (result.data) {
      if (result.data.schools && result.data.schools.length > 0) {
        setSchoolUrnList(result.data.schools);
      } else {
        schoolUrnFailedToast();
      }
      setSchoolUrnLoading(false);
    }

    if (result.error) {
      setError('An error occured, please try again.');
      setSchoolUrnLoading(false);
    }
  };

  const handleSubmitForm = async (
    values: ISchoolDetails,
    { setSubmitting, setErrors }: FormikHelpers<ISchoolDetails>
  ) => {
    setSubmitting(true);

    values.bCheckUrn = validateSchoolURN;

    if(
      values.selectedCountry &&
      (values.selectedCountry as any).value
    ){
      values.countriesId = (values.selectedCountry as any).value;
    }
    

    let valSchoolSizeResult = null;

    if (validateSchoolURN) {
      valSchoolSizeResult = await SubscribeApi.validateSchoolSize(
        values.schoolUrn,
        membershipOptInfo[0].pupilsOnRoll,
        // Membership options store the age range as selectedPackage, the free trial as ageRange
        membershipOptInfo[0].selectedPackage ||
          (membershipOptInfo[0].ageRange && membershipOptInfo[0].ageRange.value) ||
          null
      );

      if (
        valSchoolSizeResult.data &&
        !valSchoolSizeResult.data.validPupilCount
      ) {
        setOpenPupilMismatchModal(true);
      }
    }

    if (
      !validateSchoolURN ||
      (valSchoolSizeResult.data && valSchoolSizeResult.data.validPupilCount)
    ) {
      if (
        validateSchoolURN &&
        valSchoolSizeResult &&
        membershipOptInfo[0].selectedSubject &&
        membershipOptInfo[0].selectedPackage
      ) {
        // The codes the user holds now, not the ones the original quote arrived with: the package
        // step may have applied or removed some, and the re-saved quote is what later steps price.
        const heldPromoCodes = Object.keys(promoCodeSlots).reduce(
          (codes: string[], subjectsId: string) => {
            const code = promoCodeSlots[Number(subjectsId)];

            return code &&
              !codes.some((held) => held.toLowerCase() === code.toLowerCase())
              ? [...codes, code]
              : codes;
          },
          []
        );

        const saveQuoteResult = await SubscribeApi.saveQuote(
          membershipOptInfo[0].selectedPackage,
          membershipOptInfo[0].selectedSubject,
          membershipOptInfo[0].vatCountry,
          membershipOptInfo[0].vatCode,
          membershipOptInfo[0].pupilsOnRoll,
          heldPromoCodes,
        );

        if(saveQuoteResult.data && !saveQuoteResult.error){
          // The re-saved quote already describes these options, so it is read back not re-derived.
          const subTypesResult = await getWithParams(
            endpoints.public.subscription.getQuoteStatus,
            { Id: saveQuoteResult.data.id }
          );

          if (subTypesResult.response && !subTypesResult.errors) {
            const durationOptions = durationOptionsFromQuote(subTypesResult.response);
            const quotePackages = quotePackagesFromQuote(subTypesResult.response);

            const oneYearPackageObj = durationOptions.find((option) => option.subscriptionDuration === 1);
            const threeYearPackageObj = durationOptions.find((option) => option.subscriptionDuration === 3);
            const selectedOption =
              (oneYearPackageObj && selectedPackages[0].type === oneYearPackageObj.subscriptionType)
                ? oneYearPackageObj
                : threeYearPackageObj;
            const newPackageIds = (selectedOption && selectedOption.packageIds) || selectedPackages[0].packageIds;

            membershipOptInfo[0].oneYearPackage = oneYearPackageObj;
            membershipOptInfo[0].threeYearPackage = threeYearPackageObj;
            membershipOptInfo[0].quotePackages = quotePackages;
            membershipOptInfo[0].promoCodes = heldPromoCodes;
            (selectedPackages[0] as any).packageIds = newPackageIds;

            // Re-seeded from the packages this save returned: the slots are keyed by subject, and
            // the codes the save kept are the ones the later steps may price.
            dispatch(setQuotePromoCodeSlots(slotsFromQuotePackages(quotePackages)));
            dispatch(setMembershipOptInfo(membershipOptInfo));
            dispatch(updateSelectedPackages(selectedPackages));

            const result = await SubscribeApi.validateSchoolInfo(values);

            if (result.error) {
              setError(result.error.message);
              setOpenPupilMismatchModal(false);
              setSubmitting(false);
              return;
            } else {
              dispatch(setQuotesId(saveQuoteResult.data.id));
              dispatch(setSchoolDetails(values));
              onSubmit();
              setSubmitting(false);
            }
          } else {
            setError('An error occurred, please try again.');
            setOpenPupilMismatchModal(false);
            setSubmitting(false);
            return;
          }
        } else {
          setError('An error occurred, please try again.');
          setOpenPupilMismatchModal(false);
          setSubmitting(false);
          return;
        }        
      } else {
        const result = await SubscribeApi.validateSchoolInfo(values);

        if (result.error) {
          setError(result.error.message);
          setOpenPupilMismatchModal(false);
          setSubmitting(false);
          return;
        } else {
          dispatch(setSchoolDetails(values));
          onSubmit();
          setSubmitting(false);
        }
      }
    }

    if (validateSchoolURN && valSchoolSizeResult.error) {
      setError(valSchoolSizeResult.error.message);
      setOpenPupilMismatchModal(false);
      setSubmitting(false);
      return;
    }
  };

  return (
    <div className="Form">
      <FormGeneralError message={error} />
      {(pathname === AppPath.UPGRADE_SUBSCRIPTION &&
        (schoolInfo || schoolDetailsError)) ||
      pathname !== AppPath.UPGRADE_SUBSCRIPTION ? (
        <Formik
          initialValues={initialValues}
          onSubmit={(values, formikHelpers) =>
            handleSubmitForm(values, formikHelpers)
          }
          validationSchema={validationSchema}
          enableReinitialize={true}
        >
          {(formik) => {
            return (
              <Form>
                <div className="PricingColRow">
                  <div className="PCRowInner">
                    <div className="LeftCol">
                      {validateSchoolURN && (
                        <FormSchoolURNTextField
                          field={'schoolUrn'}
                          label={'School URN'}
                          required={true}
                          value={formik.values.schoolUrn}
                          searchSchoolURN={searchSchoolURN}
                          schoolUrnLoading={schoolUrnLoading}
                          schoolUrnList={schoolUrnList}
                          setSchoolUrnList={setSchoolUrnList}
                        />
                      )}
                      <div className="SideBySide">
                        <FormTextField
                          field={'schoolname'}
                          label={'School name'}
                          required={true}
                          value={formik.values.schoolname}
                        />
                        <FormTextField
                          field={'phoneNumber'}
                          label={'School phone number'}
                          required={true}
                          value={formik.values.phoneNumber}
                        />
                      </div>
                      <div className="LineSpacer4"></div>
                      <h4>School Address</h4>
                      <FormTextField
                        field={'addressLine1'}
                        label={'Address line 1'}
                        required={true}
                        value={formik.values.addressLine1}
                      />
                      <div className="SideBySide">
                        <FormTextField
                          field={'addressLine2'}
                          label={'Address line 2'}
                          required={false}
                          optionalTag={'optional'}
                          value={formik.values.addressLine2}
                        />
                        <FormTextField
                          field={'addressLine3'}
                          label={'Address line 3'}
                          required={false}
                          optionalTag={'optional'}
                          value={formik.values.addressLine3}
                        />
                      </div>
                      <div className="SideBySide Outer">
                        <div className="SideBySide">
                          <FormTextField
                            field={'townCity'}
                            label={'Town/City'}
                            required={true}
                            value={formik.values.townCity}
                          />
                          <FormTextField
                            field={'county'}
                            label={'County'}
                            required={false}
                            optionalTag={'optional'}
                            value={formik.values.county}
                          />
                        </div>
                        <div className="SideBySide">
                          <FormTextField
                            field={'postcodeZipcode'}
                            label={postcodeLabel}
                            required={true}
                            value={formik.values.postcodeZipcode}
                          />
                          <div
                            className={
                              'FormBox CountryListBlock' +
                              ((formik.touched.country && formik.errors.country) ? ' ErrorFB' : '') +
                              (vatCountry !== null ? ' DisabledCLField' : '')
                            }
                          >
                            <div className="FormLabel">
                              <label htmlFor="CountryList">Country</label>
                            </div>
                            <div className="FormField">
                              <Dropdown
                                id={'country'}
                                options={formik.values.countryList || []}
                                value={formik.values.selectedCountry || null}
                                onChange={(e: SelectOption) =>
                                  formik.setFieldValue('selectedCountry', e)
                                }
                                customClassName="EditSingleSelect"
                                customNoOptionsMessage="No options"
                                placeholder={'Please select'}
                                roundedRectangle={true}
                                readOnly={vatCountry !== null}
                              />
                              <ErrorM name="country" />
                            </div>
                          </div>
                        </div>
                      </div>
                      {hideSummaryPanel && (
                        <button
                          className="Btn BtnOrange"
                          type="submit"
                          disabled={formik.isSubmitting}
                        >
                          Save and continue
                        </button>
                      )}
                    </div>
                    {!hideSummaryPanel && (
                      <div className="RightCol">
                        <PricingSummaryDetails
                          addPromoStep={false}
                          isDisabled={formik.isSubmitting}
                          btnText="Save and continue"
                          existingMembershipOptInfo={membershipOptInfo}
                          {...(heldQuotePackages && heldQuotePackages.length > 0
                            ? { promoCodeSlots, quotePackages: heldQuotePackages }
                            : {})}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {openPupilMismatchModal && (
                  <PupilNumMismatchModal
                    panelOpen={openPupilMismatchModal}
                    togglePanelOpen={setOpenPupilMismatchModal}
                    submitForm={formik.submitForm}
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      ) : (
        <div
          style={{
            minHeight: ' calc(100vh - 300px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default SchoolDetailsForm;
